import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
  parseFractionLikeInput,
} from "../../../../components/inputs/FractionInput";

const VARIANTS = [
  { relationship: "volume_to_surface", volume1: 8, scale: 2 },
  { relationship: "volume_to_surface", volume1: 27, scale: 2 },
  { relationship: "volume_to_surface", volume1: 64, scale: 2 },
  { relationship: "length_to_surface", volume1: 8, scale: 2 },
  { relationship: "length_to_surface", volume1: 27, scale: 3 },
  { relationship: "length_to_volume", volume1: 8, scale: 2 },
  { relationship: "length_to_volume", volume1: 27, scale: 3 },
];

const SOLIDS = [
  { nominative: "Ostrosłup", genitive: "ostrosłupa" },
  { nominative: "Graniastosłup", genitive: "graniastosłupa" },
  { nominative: "Prostopadłościan", genitive: "prostopadłościanu" },
];

function pick(items) {
  return items[Math.floor(Math.random() * items.length)];
}

export function buildSimilarSolidData({ relationship = "volume_to_surface", volume1, scale, solid }) {
  return { relationship, volume1, volume2: volume1 * scale ** 3, scale, surfaceRatio: scale ** 2, solid, maxPoints: 1 };
}

function createTask({ relationship = "volume_to_surface", volume1, scale, source, solid = pick(SOLIDS) }) {
  const volume2 = volume1 * scale ** 3;
  const surfaceRatio = scale ** 2;
  const answerValue = relationship === "length_to_volume" ? volume2 / volume1 : surfaceRatio;
  return {
    type: "MaturaSimilarPyramidsSurfaceScaleOpenTask",
    data: buildSimilarSolidData({ relationship, volume1, scale, solid }),
    answer: { raw: String(answerValue), parsed: answerValue },
    ...(source ? { source } : {}),
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2024_MAJ") {
    return createTask({
      volume1: 64,
      scale: 2,
      relationship: "volume_to_surface",
      solid: SOLIDS[0],
      source: {
        variant: "original",
        exam: "MATURA",
        session: options.session,
        taskNumber: 26,
      },
    });
  }

  return createTask(pick(VARIANTS));
}

export function renderTask({ data }) {
  const { nominative, genitive } = data.solid || SOLIDS[0];
  if (data.relationship === "length_to_surface") {
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>{nominative}{" "}<InlineMath math="F_1" />{" "}jest podobny do {genitive}{" "}<InlineMath math="F_2" />.</p>
        <p>
          Stosunek długości odpowiadających sobie krawędzi {genitive}{" "}<InlineMath math="F_2" />{" "}
          do długości odpowiadających sobie krawędzi {genitive}{" "}<InlineMath math="F_1" />{" "}jest równy{" "}
          <InlineMath math={`${data.scale}`} />.
        </p>
        <p>
          Stosunek pól powierzchni całkowitych {genitive}{" "}<InlineMath math="F_2" />{" "}i {genitive}{" "}
          <InlineMath math="F_1" />{" "}jest równy
        </p>
      </div>
    );
  }

  if (data.relationship === "length_to_volume") {
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>{nominative}{" "}<InlineMath math="F_1" />{" "}jest podobny do {genitive}{" "}<InlineMath math="F_2" />.</p>
        <p>
          Stosunek długości odpowiadających sobie krawędzi {genitive}{" "}<InlineMath math="F_2" />{" "}
          do długości odpowiadających sobie krawędzi {genitive}{" "}<InlineMath math="F_1" />{" "}jest równy{" "}
          <InlineMath math={`${data.scale}`} />.
        </p>
        <p>
          Stosunek objętości {genitive}{" "}<InlineMath math="F_2" />{" "}do objętości {genitive}{" "}
          <InlineMath math="F_1" />{" "}jest równy
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>{nominative}{" "}<InlineMath math="F_1" />{" "}jest podobny do {genitive}{" "}<InlineMath math="F_2" />.</p>
      <p>
        Objętość {genitive}{" "}<InlineMath math="F_1" />{" "}jest równa{" "}<InlineMath math={`${data.volume1}`} />,
        a objętość {genitive}{" "}<InlineMath math="F_2" />{" "}jest równa{" "}<InlineMath math={`${data.volume2}`} />.
      </p>
      <p>
        Stosunek pola powierzchni całkowitej {genitive}{" "}<InlineMath math="F_2" />{" "}do pola
        powierzchni całkowitej {genitive}{" "}<InlineMath math="F_1" />{" "}jest równy
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer?.raw ?? userAnswer ?? ""}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="np. 4"
    />
  );
}

function parsedValue(userAnswer) {
  if (Number.isFinite(userAnswer?.parsed)) return userAnswer.parsed;
  return parseFractionLikeInput(String(userAnswer?.raw ?? userAnswer ?? ""));
}

export function checkAnswer({ answer }, userAnswer) {
  const value = parsedValue(userAnswer);
  return Number.isFinite(value) && Math.abs(value - answer.parsed) < 1e-9;
}

export function renderAnswer({ answer }) {
  return <InlineMath math={`${answer.parsed}`} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = userAnswer?.raw ?? userAnswer ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(raw)} />;
}

export function renderExplanation({ data }) {
  const { relationship, volume1, volume2, scale, surfaceRatio } = data;
  if (relationship === "length_to_surface") {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p>
          <strong>Krok 1.</strong> Stosunek długości odpowiadających sobie krawędzi jest skalą brył:
        </p>
        <BlockMath math={`k=${scale}`} />
        <p>
          <strong>Krok 2.</strong> Stosunek pól powierzchni brył podobnych jest kwadratem skali:
        </p>
        <BlockMath math={`\\frac{P_{c2}}{P_{c1}}=k^2=${scale}^2=${surfaceRatio}`} />
      </div>
    );
  }

  if (relationship === "length_to_volume") {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p>
          <strong>Krok 1.</strong> Stosunek długości odpowiadających sobie krawędzi jest skalą brył:
        </p>
        <BlockMath math={`k=${scale}`} />
        <p>
          <strong>Krok 2.</strong> Stosunek objętości brył podobnych jest sześcianem skali:
        </p>
        <BlockMath math={`\\frac{V_2}{V_1}=k^3=${scale}^3=${volume2 / volume1}`} />
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Dla brył podobnych stosunek objętości jest sześcianem skali:
      </p>
      <BlockMath math={`\\frac{V_2}{V_1}=k^3`} />
      <BlockMath math={`\\frac{${volume2}}{${volume1}}=${volume2 / volume1}=${scale}^3,\\qquad k=${scale}`} />
      <p>
        <strong>Krok 2.</strong> Stosunek pól powierzchni jest kwadratem skali — analogicznie
        do podobnych figur na płaszczyźnie:
      </p>
      <BlockMath math={`\\frac{P_{c2}}{P_{c1}}=k^2=${scale}^2=${surfaceRatio}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
